import type { AdminRole } from "../api/adminAuthApi";

/**
 * sessionStorage cache for the caller's own role + display name from `GET /admin/auth/me`.
 * Deliberately *not* set at login — verify doesn't carry `role` / reliable `full_name` for this
 * header. Cleared on sign-out via `endAdminSession`.
 */
export const ADMIN_SESSION_ROLE_KEY = "ns-admin-session-role";
export const ADMIN_SESSION_FULL_NAME_KEY = "ns-admin-session-full-name";
export const ADMIN_SESSION_PHONE_KEY = "ns-admin-session-phone";

const NULL_ROLE_SENTINEL = "__null__";

const listeners = new Set<() => void>();

export function subscribeAdminSessionRole(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

/** `undefined` = not fetched yet this session; `null` = fetched, server says no role. */
export function getAdminSessionRole(): AdminRole | undefined {
  try {
    const raw = sessionStorage.getItem(ADMIN_SESSION_ROLE_KEY);
    if (raw === null) {
      return undefined;
    }
    return raw === NULL_ROLE_SENTINEL ? null : (raw as AdminRole);
  } catch {
    return undefined;
  }
}

export function getAdminSessionFullName(): string | undefined {
  try {
    const raw = sessionStorage.getItem(ADMIN_SESSION_FULL_NAME_KEY);
    return raw === null ? undefined : raw;
  } catch {
    return undefined;
  }
}

export function getAdminSessionPhone(): string | undefined {
  try {
    const raw = sessionStorage.getItem(ADMIN_SESSION_PHONE_KEY);
    return raw === null ? undefined : raw;
  } catch {
    return undefined;
  }
}

export function setAdminSessionProfile(input: {
  role: AdminRole;
  fullName?: string | null;
  phone?: string | null;
}): void {
  try {
    sessionStorage.setItem(
      ADMIN_SESSION_ROLE_KEY,
      input.role ?? NULL_ROLE_SENTINEL,
    );
    const name = input.fullName?.trim();
    if (name) {
      sessionStorage.setItem(ADMIN_SESSION_FULL_NAME_KEY, name);
    } else {
      sessionStorage.removeItem(ADMIN_SESSION_FULL_NAME_KEY);
    }
    const phone = input.phone?.trim();
    if (phone) {
      sessionStorage.setItem(ADMIN_SESSION_PHONE_KEY, phone);
    } else {
      sessionStorage.removeItem(ADMIN_SESSION_PHONE_KEY);
    }
  } catch {
    /* private mode / quota */
  }
  notifyListeners();
}

/** @deprecated Prefer `setAdminSessionProfile` so the header can show full_name too. */
export function setAdminSessionRole(role: AdminRole): void {
  setAdminSessionProfile({ role });
}

export function clearAdminSessionRole(): void {
  try {
    sessionStorage.removeItem(ADMIN_SESSION_ROLE_KEY);
    sessionStorage.removeItem(ADMIN_SESSION_FULL_NAME_KEY);
    sessionStorage.removeItem(ADMIN_SESSION_PHONE_KEY);
  } catch {
    /* noop */
  }
  notifyListeners();
}

/** Human label for the top-bar / account menu (from `admin` | `super_admin`). */
export function formatAdminRoleLabel(role: AdminRole | undefined): string {
  if (role === "super_admin") {
    return "Operations Lead · Super Admin";
  }
  if (role === "admin") {
    return "Admin";
  }
  if (role === null) {
    return "Staff";
  }
  return "";
}
